import { rootApi } from 'src/services/rootApi';
import { HTTPRequestType } from 'src/services/api.types';
import { readEventStream, streamAuthHeaders } from 'src/services/sse';

/**
 * The CRM assistant (Phase 4 of JT1-1704): the streamed chat (JT1-1779), saved conversations
 * (JT1-1776) and confirming the changes it proposes (JT1-1780). The shapes mirror
 * `AssistantService`, `AssistantConversationStore` and `AssistantActionsService` in the backend.
 */

export type AssistantActionStatus = 'pending' | 'done' | 'partly_done' | 'cancelled' | 'failed' | 'expired';

export interface AssistantActionRecord {
  kind: 'candidate' | 'contact' | 'company' | 'deal' | 'job' | 'application';
  id: string;
  name: string;
  url: string | null;
}

/** What a confirmation card shows. */
export interface AssistantActionSummary {
  title: string;
  fields: { label: string; value: string }[];
  record: AssistantActionRecord | null;
  /** Several applications moved at once. */
  applications?: { candidate: string; job: string | null; from: string; url: string | null }[];
  warning?: string | null;
  /** Which details the recruiter may change before confirming. */
  editable: AssistantEditableField[];
  /** What the edit form starts from. */
  values?: { title?: string; type?: string; due_at?: string | null; text?: string; notify_candidate?: boolean };
}

export type AssistantEditableField = 'title' | 'due_date' | 'due_time' | 'type' | 'text' | 'notify_candidate';

export interface AssistantAction {
  id: string;
  kind: 'create_task' | 'log_note' | 'move_stage';
  status: AssistantActionStatus;
  summary: AssistantActionSummary;
  result: { message?: string; url?: string | null; moved?: string[]; failed?: { candidate: string; reason: string }[] } | null;
  error: string | null;
  created_at: string;
  decided_at: string | null;
}

export interface AssistantActionEdits {
  title?: string;
  due_date?: string;
  due_time?: string;
  type?: string;
  text?: string;
  notify_candidate?: boolean;
}

export type AssistantEvent =
  | { type: 'conversation'; id: string; title: string }
  | { type: 'text'; delta: string }
  | { type: 'tool'; id: string; name: string; label: string; status: 'running' | 'done' | 'failed' }
  | { type: 'action'; action: AssistantAction }
  | { type: 'done'; text: string; stopped: string; steps: number; conversation_id: string }
  | { type: 'error'; message?: string; status?: number };

export interface AssistantConversationSummary {
  id: string;
  title: string;
  last_message_at: string;
}

export interface AssistantDisplayMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  tools_used: { name: string; label: string }[];
  created_at: string;
}

export interface AssistantConversation extends AssistantConversationSummary {
  messages: AssistantDisplayMessage[];
  actions: AssistantAction[];
}

export const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const malformedError = (data: string): AssistantEvent => ({ type: 'error', message: data });

/**
 * Asks the assistant one question and calls `onEvent` for each event until the answer ends. A POST
 * read with fetch, carrying the login and takeover headers, as the sourcing stream does (JT1-1747).
 */
export async function streamAssistant(
  { message, conversationId, signal }: { message: string; conversationId?: string | null; signal?: AbortSignal },
  onEvent: (event: AssistantEvent) => void,
): Promise<void> {
  const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/ai/assistant/chat`, {
    method: 'POST',
    headers: { ...streamAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, ...(conversationId ? { conversation_id: conversationId } : {}), timezone: browserTimezone() }),
    signal,
  });

  if (!response.ok || !response.body) {
    onEvent({ type: 'error', status: response.status });
    return;
  }

  const finished = await readEventStream<AssistantEvent>(
    response,
    onEvent,
    (event) => event.type === 'done' || event.type === 'error',
    malformedError,
  );

  // A stream that closes without an answer would otherwise leave the window waiting forever.
  if (!finished) onEvent({ type: 'error', status: 0 });
}

export const aiAssistantApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getAssistantConversations: builder.query<AssistantConversationSummary[], void>({
      query: () => ({ url: '/ai/assistant/conversations' }),
      providesTags: ['AssistantConversations'],
    }),

    getAssistantConversation: builder.query<AssistantConversation, string>({
      query: (id) => ({ url: `/ai/assistant/conversations/${id}` }),
    }),

    deleteAssistantConversation: builder.mutation<void, string>({
      query: (id) => ({ url: `/ai/assistant/conversations/${id}`, method: HTTPRequestType.DELETE }),
      invalidatesTags: ['AssistantConversations'],
    }),

    confirmAssistantAction: builder.mutation<AssistantAction, { id: string; edits?: AssistantActionEdits }>({
      query: ({ id, edits }) => ({
        url: `/ai/assistant/actions/${id}/confirm`,
        method: HTTPRequestType.POST,
        data: { edits: edits ?? {}, timezone: browserTimezone() },
      }),
      // A confirmed task or note shows up on the activity page's digest and task card.
      invalidatesTags: ['AiDigest'],
    }),

    cancelAssistantAction: builder.mutation<AssistantAction, string>({
      query: (id) => ({ url: `/ai/assistant/actions/${id}/cancel`, method: HTTPRequestType.POST }),
    }),
  }),
});

export const {
  useGetAssistantConversationsQuery,
  useLazyGetAssistantConversationQuery,
  useDeleteAssistantConversationMutation,
  useConfirmAssistantActionMutation,
  useCancelAssistantActionMutation,
} = aiAssistantApi;
